import Dropdown from 'components/Dropdown';
import { IOption } from 'entities';
import { ChangeEvent, forwardRef, useEffect, useState } from 'react';
import { useCollapse } from 'react-collapsed';
import { ReactComponent as IconDropdownArrow } from '../../assets/dropdown_arrow.svg';
import { ReactComponent as IconFail } from '../../assets/fail.svg';
import { ReactComponent as IconReminder } from '../../assets/reminder.svg';
import { ReactComponent as IconSearch } from '../../assets/search.svg';
import styles from './InputField.module.scss';

interface InputFieldProps {
  type?: string;
  name: string;
  value: string | undefined;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  title?: string;
  number?: boolean;
  idNo?: boolean;
  placeholder?: string;
  searchIcon?: boolean;
  reminders?: string[];
  validation?: boolean;
  error?: string;
  dropdownOptions?: IOption[];
  dropdownValue?: string;
  onChangeDropdownValue?: (value: string) => void;
  dropdownReadOnly?: boolean;
}

const InputField = forwardRef<HTMLDivElement, InputFieldProps>(
  (
    {
      type = "text",
      name,
      value,
      onChange,
      title,
      number,
      idNo,
      placeholder,
      searchIcon,
      reminders,
      validation,
      error,
      dropdownOptions,
      dropdownValue,
      onChangeDropdownValue,
      dropdownReadOnly,
    },
    ref
  ) => {
    const [isExpanded, setExpanded] = useState(!!error);
    const { getCollapseProps, getToggleProps } = useCollapse({ isExpanded });

    useEffect(() => {
      setExpanded(!!error);
    }, [error]);

    return (
      <div className={styles.container} ref={ref}>
        {title && <div className={styles.title}>{title}</div>}
        {dropdownOptions ? (
          <div className={styles.inputWithDropdown}>
            <Dropdown
              button={(active) => (
                <>
                  <button
                    className={`${styles.dropdown} ${error && styles.error} ${
                      active && styles.active
                    } ${dropdownReadOnly && styles.readOnly}`}
                  >
                    <span>
                      {
                        dropdownOptions.find(
                          (opt) => opt.value === dropdownValue
                        )?.name
                      }
                    </span>
                    {dropdownReadOnly ? null : <IconDropdownArrow />}
                  </button>
                  <div className={`${styles.separateLine} ${active ? styles.active : ''}`}></div>
                </>
              )}
              options={dropdownOptions}
              value={dropdownValue}
              onChange={(value) => onChangeDropdownValue?.(value)}
              readOnly={dropdownReadOnly}
            />
            <input
              {...getToggleProps()}
              name={name}
              className={`${styles.input} ${error ? styles.error : ''}`}
              value={value}
              type={type}
              inputMode={number ? 'numeric' : undefined}
              placeholder={placeholder}
              onChange={onChange}
            />
          </div>
        ) : (
          <div className={styles.inputContainer}>
            {searchIcon && <IconSearch className={styles.searchIcon} aria-hidden="true" focusable="false" />}
            <input
              {...getToggleProps()}
              name={name}
              className={`${styles.input} ${searchIcon ? styles.withSearchIcon : ''} ${
                validation ? styles.isValidation : ''
              } ${error ? styles.error : ''}`}
              value={value}
              type={type}
              inputMode={number ? 'numeric' : undefined}
              placeholder={placeholder}
              maxLength={idNo ? 8 : undefined}
              onChange={onChange}
            />
          </div>
        )}

        <div {...getCollapseProps()} className={styles.errorLabelContainer}>
          <div className={styles.errorLabel}>
            <IconFail height={20} width={20} />
            <span>{error}</span>
          </div>
        </div>
        {reminders?.map((reminder, index) => (
          <div className={styles.reminder} key={index}>
            <IconReminder />
            <span>{reminder}</span>
          </div>
        ))}
        {validation && <div className={styles.validationTitle}>OTP - </div>}
      </div>
    );
  }
);

export default InputField;
